import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { Container, Row, Col, Button, Badge, Nav } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Header from '../../components/user/common/Header';
import Footer from '../../components/common/Footer';
import SpinnerLoader from '../../components/common/SpinnerLoader';
import PageTitle from '../../components/user/common/PageTitle';

const BuyerOrdersList = () => {
    const history = useHistory();
    const authInfo = JSON.parse(localStorage.getItem('authInfo'));
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [filter, setFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProjects, setTotalProjects] = useState(0);
    const limit = 10;

    useEffect(() => {
        setCurrentPage(1); // Reset to first page when filter changes
        fetchProjects(1);
    }, [filter]);

    useEffect(() => {
        fetchProjects(currentPage);
    }, [currentPage]);

    const fetchProjects = async (page = 1) => {
        setLoading(true);
        try {
            // Build query parameters based on current filter
            const params = new URLSearchParams();
            
            // Map frontend filters to backend parameters
            switch (filter) {
                case 'services':
                    params.append('type', 'services');
                    break;
                case 'products':
                    params.append('type', 'products');
                    break;
                case 'active':
                    params.append('status', 'in-progress');
                    break;
                case 'awaiting':
                    params.append('status', 'pending');
                    break;
                case 'completed':
                    params.append('status', 'completed');
                    break;
                case 'cancelled':
                    params.append('status', 'cancelled');
                    break;
                case 'draft':
                    params.append('status', 'draft');
                    break;
                case 'open':
                    params.append('status', 'open');
                    break;
                // 'all' case - no additional filters
            }

            // Add pagination
            params.append('page', page.toString());
            params.append('limit', limit.toString());

            const queryString = params.toString();
            const url = `/user/projects/${authInfo.id}${queryString ? `?${queryString}` : ''}`;
            
            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authInfo.token}` },
            });
            
            // Handle both old and new response formats
            if (res.data.projects) {
                setProjects(res.data.projects);
                console.log("SET PROJECTS: ", projects)
                // Set pagination info if available from enhanced API
                if (res.data.totalReturned !== undefined) {
                    setTotalProjects(res.data.totalReturned);
                    setTotalPages(Math.ceil(res.data.totalReturned / limit));
                }
            } else {
                setProjects(res.data.projects || []);
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
            toast.error('Failed to load your contracts.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (iso) =>
        new Date(iso).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });

    // Since we're now filtering on the backend, we can remove client-side filtering
    // or keep it as a fallback for backward compatibility
    const filtered = projects.filter((p) => {
        // Fallback client-side filtering (in case backend doesn't support all filters yet)
        if (filter === 'services') return p.isProductProject === false;
        if (filter === 'products') return p.isProductProject === true;
        if (filter === 'active') return p.status === 'in-progress';
        if (filter === 'awaiting') return p.status === 'pending';
        if (filter === 'completed') return p.status === 'completed';
        if (filter === 'cancelled') return p.status === 'cancelled';
        if (filter === 'draft') return p.status === 'draft';
        if (filter === 'open') return p.status === 'open';
        return true;
    });

    // start chat with seller
    const handleSupportChat = async (seller) => {
        const authInfo = JSON.parse(localStorage.getItem('authInfo'));
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const user_id = authInfo.id

        try {
            const url = '/user/accessChat';
            axios.post(url, {
                receiverId: {
                    id: seller?._id,
                    name: seller?.name,
                    image_url: seller?.image_url || "",
                    // isSupport: false,
                },
                authorId: {
                    id: user_id,
                    name: userInfo.name,
                    image_url: userInfo.imgUrl,
                }
            }, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': `Bearer ${authInfo.token}`
                }
            }).then((response) => {
                console.log("response", response)

                if (response.data.status === true) {
                    history.push({
                        pathname: `/chat`,
                        state: {
                            targetUserId: seller?._id,
                            sellerId: seller?._id,
                        }
                    });
                }
            }).catch((error) => {
                console.log("error", error);
            });
        } catch (error) {
            console.log("error", error)
        }
    }

    const renderStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return <Badge bg="success">{status?.toUpperCase()}</Badge>;
            case 'in-progress':
                return <Badge bg="warning">{status?.toUpperCase()}</Badge>;
            case 'pending':
                return <Badge bg="secondary">{status?.toUpperCase()}</Badge>;
            case 'out-for-delivery':
                return <Badge bg="primary" text="dark">{status?.toUpperCase()}</Badge>;
            case 'cancelled':
                return <Badge bg="danger">{status?.toUpperCase()}</Badge>;
            case 'draft':
                return <Badge bg="light" text="dark">{status?.toUpperCase()}</Badge>;
            case 'open':
                return <Badge bg="info">{status?.toUpperCase()}</Badge>;
            default:
                return <Badge bg="secondary">{status?.toUpperCase()}</Badge>;
        }
    };

    if (loading) return <SpinnerLoader />;

    return (
        <>
            <Header />
            <PageTitle title="My Order" />
            <Container className="pt-3 pb-5">
                <Nav variant="pills" activeKey={filter} onSelect={setFilter} className="justify-content-center mb-4">
                    <Nav.Item><Nav.Link eventKey="all">All</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="services">Service Order</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="products">Product Orders</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="active">Active Contracts</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="awaiting">Awaiting payments</Nav.Link></Nav.Item>
                </Nav>

                <Row className="g-4">
                    
                    {filtered.length > 0 ? (
                        filtered.map((proj) => {
                            const isService = !proj.isProductProject;
                            const dateRange = `${formatDate(proj.createdAt)} - ${proj.status === 'completed' ? formatDate(proj.updatedAt) : 'Present'
                                }`;
                            // Use the enhanced milestone data from backend if available
                            const itemsCount = proj.totalItems || 
                                (proj.totalMilestones !== undefined && proj.completedMilestones !== undefined ? 
                                    `${proj.completedMilestones}/${proj.totalMilestones}` : 
                                    `${proj.completedMilestones || 0}/${proj.totalMilestones || 0}`);
                            const labelColor = isService ? 'primary' : 'warning';

                            return (
                                <Col md={12} key={proj._id}>
                                    <div className="border rounded-4 p-4 shadow-sm d-flex flex- flex-md-row justify-content-between align-items-start gap-3 bg-white">
                                        <div className="d-flex flex-column gap-4">
                                            <div className="d-flex gap-3 align-items-center flex-wrap">
                                                <Badge bg={labelColor}>{isService ? 'Service' : 'Product'}</Badge>
                                                <small className="text-muted">{dateRange}</small>
                                            </div>
                                            <h5 className="fw-semibold mb-1">{proj.title}</h5>
                                            <div className="bold text-muted">
                                                {/* Show seller name if available from enhanced API */}
                                                {proj.sellerId?.name && (
                                                    <>Seller: <strong>{proj.sellerId.name}</strong></>
                                                )}
                                                <br />
                                            </div>
                                            <div className='d-flex gap-2 align-items-center'>
                                                <small className="text-muted">{isService ? 'Milestones' : 'Items'}: {itemsCount}</small>
                                            </div>
                                        </div>
                                        <div className="text-end mt-2 mt-md-0" style={{
                                            minHeight: '150px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between'
                                        }}>
                                            <div className="d-flex justify-content-between align-items-center gap-3">
                                                <div className='flex'>
                                                    <div className="fw-bold fs-5">Total Payment :${proj.budget.toFixed(2)}</div>
                                                </div>
                                                <div className="align-self-start">
                                                    {renderStatusBadge(proj.status)}
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2 justify-content-end mt-3">
                                                <Button
                                                    variant="outline-dark"
                                                    className="rounded-pill px-4"
                                                    onClick={() => history.push(`/checkout/milestones/${proj._id}`)}
                                                >
                                                    {isService ? 'View Contract' : 'View Detail'}
                                                </Button>
                                                <Button variant="warning" className="rounded-pill btn_yellow px-4" onClick={() => handleSupportChat(proj.sellerId)}>Message</Button>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            );
                        })
                    ) : (
                        <Col><p className="text-center text-muted">No orders found.</p></Col>
                    )}
                </Row>

                {/* Pagination Controls */}
                { (
                    <Row className="mt-4">
                        <Col className="d-flex justify-content-center align-items-center">
                            <div className="d-flex align-items-center gap-3">
                                {/* Previous Button */}
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    className="d-flex align-items-center gap-1"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="15,18 9,12 15,6"></polyline>
                                    </svg>
                                    Previous
                                </Button>

                                {/* Page Info */}
                                <div className="d-flex align-items-center gap-2">
                                    <span className="text-muted small">Page</span>
                                    <Badge bg="light" text="dark" className="px-3 py-2">
                                        {currentPage} of {totalPages}
                                    </Badge>
                                    <span className="text-muted small">
                                        ({totalProjects} total orders)
                                    </span>
                                </div>

                                {/* Next Button */}
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    className="d-flex align-items-center gap-1"
                                >
                                    Next
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="9,18 15,12 9,6"></polyline>
                                    </svg>
                                </Button>
                            </div>
                        </Col>
                    </Row>
                )}
            </Container>
            <Footer />
        </>
    );
};

export default BuyerOrdersList;